import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE = 'Softlogic Australia';
const DEFAULT_DESC = 'Softlogic Australia — Where Innovation Meets Efficiency. One stop destination for all your software needs including Chefmax, RightIOT, Cyber Security and Digital Transformation services.';
const DEFAULT_KEYWORDS = 'software solutions Australia, IT company Melbourne, Chefmax, RightIOT, cyber security, digital transformation, RPA, healthcare software, IoT solutions, smart homes';
const SITE_URL = 'https://softlogic.com.au';
const OG_IMAGE = `${SITE_URL}/uploads/og-default.webp`;

const ORG_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Softlogic Australia",
  "url": SITE_URL,
  "logo": `${SITE_URL}/images/logo/softlogic-logo.webp`,
  "description": DEFAULT_DESC,
  "address": { "@type": "PostalAddress", "streetAddress": "Unit 33 15 Ricketts Rd", "addressLocality": "Mount Waverley", "addressRegion": "VIC", "postalCode": "3149", "addressCountry": "AU" },
  "contactPoint": { "@type": "ContactPoint", "telephone": "(03) 9543 7566", "contactType": "customer service", "email": "sales@softlogic.com.au", "areaServed": "AU" },
  "sameAs": ["https://www.facebook.com/AustraliaSoftlogic", "https://www.linkedin.com/company/australia-softlogic/"]
};

export default function SEO({ title, description, keywords, image, url, type = 'website', noIndex = false, structuredData }) {
  // Avoid "... | Softlogic | Softlogic Australia" duplication
  const fullTitle = title
    ? (title.toLowerCase().includes('softlogic') ? title : `${title} | ${SITE}`)
    : `${SITE} | Where Innovation Meets Efficiency`;
  const desc = description || DEFAULT_DESC;
  const kw   = keywords   || DEFAULT_KEYWORDS;
  const img  = image      || OG_IMAGE;
  const canonical = url ? `${SITE_URL}${url}` : SITE_URL;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description"        content={desc} />
      <meta name="keywords"           content={kw} />
      <meta name="author"             content="Softlogic Australia" />
      <meta name="language"           content="en-AU" />
      <link rel="canonical"           href={canonical} />

      {/* Allow Google rich snippets & large image previews */}
      {noIndex
        ? <meta name="robots" content="noindex, nofollow" />
        : <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      }

      {/* Local SEO — Melbourne, Victoria, Australia */}
      <meta name="geo.region"    content="AU-VIC" />
      <meta name="geo.placename" content="Melbourne, Victoria, Australia" />
      <meta name="geo.position"  content="-37.876;145.130" />
      <meta name="ICBM"          content="-37.876, 145.130" />

      {/* Open Graph */}
      <meta property="og:title"       content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:image"       content={img} />
      <meta property="og:url"         content={canonical} />
      <meta property="og:type"        content={type} />
      <meta property="og:site_name"   content={SITE} />
      <meta property="og:locale"      content="en_AU" />

      {/* Twitter */}
      <meta name="twitter:card"        content="summary_large_image" />
      <meta name="twitter:title"       content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image"       content={img} />

      {/* Organisation schema always present */}
      <script type="application/ld+json">{JSON.stringify(ORG_SCHEMA)}</script>

      {/* Optional per-page structured data (product, article, etc.) */}
      {structuredData && (
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      )}
    </Helmet>
  );
}
